/**
 * Shared visual constants. Every screen — game, menu, scoreboard — measures
 * against this one grid, so nothing drifts out of alignment.
 *
 * The whole interface is drawn from one primitive: a chamfered block with a
 * lighter top face and a darker bottom face, corners mitred at 45°. Playfield
 * cells, panels, buttons, chips and rank badges are all the same routine called
 * with a size and a colour, so the language stays consistent by construction.
 *
 * Two shells are defined: `night` (deep navy, default) and `day` (warm paper).
 * Their face colours are precomputed, so the render loop never mixes at runtime.
 */

import { TUNING } from '../core/game.ts';

/**
 * Which shell is painted. Named `ShellTheme` rather than the handoff's `Shell`
 * because `app/shell.ts` already owns that name.
 */
export type ShellTheme = 'night' | 'day';

/** The four mitred faces of a block, plus its base fill. */
export interface Faces {
  bg: string;
  top: string;
  left: string;
  right: string;
  bottom: string;
}

/** Multiply toward white (`a >= 0`) or toward black (`a < 0`). */
export function mix(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  const f = (v: number) => (a >= 0 ? Math.round(v + (255 - v) * a) : Math.round(v * (1 + a)));
  return `#${ch.map((v) => f(v).toString(16).padStart(2, '0')).join('')}`;
}

/** The one derivation rule: +20 top, +8 left, -10 right, -22 bottom. */
export function faces(hex: string): Faces {
  return {
    bg: hex,
    top: mix(hex, 0.2),
    left: mix(hex, 0.08),
    right: mix(hex, -0.1),
    bottom: mix(hex, -0.22),
  };
}

/** Pressed / recessed surfaces: the same rule with the faces flipped. */
export function facesInset(hex: string): Faces {
  const f = faces(hex);
  return { bg: f.bg, top: f.bottom, left: f.right, right: f.left, bottom: f.top };
}

/** WCAG relative luminance, 0–1. */
export function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const linear = (value: number) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * linear((n >> 16) & 0xff) +
    0.7152 * linear((n >> 8) & 0xff) +
    0.0722 * linear(n & 0xff)
  );
}

function contrast(a: string, b: string): number {
  const light = Math.max(luminance(a), luminance(b));
  const dark = Math.min(luminance(a), luminance(b));
  return (light + 0.05) / (dark + 0.05);
}

/** Label colours legible on a block of `hex`. */
export function onBlock(hex: string): { text: string; dim: string } {
  const candidates = [palette().onColor, '#000000', '#ffffff'] as const;
  const text = candidates.reduce((best, candidate) =>
    contrast(candidate, hex) > contrast(best, hex) ? candidate : best,
  );
  return { text, dim: text };
}

export interface ShellPalette {
  background: string;
  field: string;
  cellEmpty: Faces;
  well: Faces;
  panel: Faces;
  panelAlt: Faces;
  rule: string;
  text: string;
  textDim: string;
  textLabel: string;
  textFaint: string;
  accent: string;
  accentFaces: Faces;
  /** Text drawn on an accent block. */
  onAccent: string;
  /** The quieter second label on an accent block. */
  onAccentDim: string;
  /** Text drawn on a coloured block (buttons, badges). */
  onColor: string;
  scrim: string;
  ghost: Faces;
  /** Row tint for the just-saved scoreboard entry. */
  highlightRow: string;
  /** Band over the vacated rows during a line-clear collapse. */
  clearBand: string;
  /** Field tint at full strength during the level-up flash. */
  levelFlash: string;
}

export const NIGHT: ShellPalette = {
  background: '#14202c',
  field: '#0f1922',
  cellEmpty: { bg: '#16222e', top: '#101a24', left: '#131d27', right: '#1b2836', bottom: '#20303f' },
  well: { bg: '#0f1922', top: '#0a1119', left: '#0d1620', right: '#1c2833', bottom: '#25333f' },
  panel: { bg: '#1a2735', top: '#2f4254', left: '#263748', right: '#16212c', bottom: '#121b24' },
  panelAlt: { bg: '#1d2b39', top: '#33475a', left: '#2a3d4f', right: '#16222e', bottom: '#121d26' },
  rule: '#0e1720',
  text: '#ffffff',
  textDim: '#93a8ba',
  textLabel: '#7e94a8',
  /** Was `#5c7185` — 3.4:1 on the shell background. */
  textFaint: '#7a90a4',
  accent: '#efdc38',
  accentFaces: faces('#efdc38'),
  onAccent: '#14202c',
  onAccentDim: '#625400',
  onColor: '#14202c',
  scrim: 'rgba(15,25,34,0.90)',
  ghost: {
    bg: 'rgba(255,255,255,0.05)',
    top: 'rgba(255,255,255,0.20)',
    left: 'rgba(255,255,255,0.13)',
    right: 'rgba(255,255,255,0.08)',
    bottom: 'rgba(255,255,255,0.06)',
  },
  highlightRow: 'rgba(239,220,56,0.10)',
  clearBand: 'rgba(239,220,56,0.22)',
  levelFlash: 'rgba(239,220,56,0.16)',
};

export const DAY: ShellPalette = {
  background: '#f5f1e8',
  field: '#e5e0d5',
  cellEmpty: { bg: '#e5e0d5', top: '#cfcabc', left: '#d9d4c7', right: '#eeebe3', bottom: '#f4f1ea' },
  well: { bg: '#e5e0d5', top: '#c6c1b3', left: '#d2ccbe', right: '#eeebe3', bottom: '#f6f3ec' },
  panel: { bg: '#faf7f0', top: '#ffffff', left: '#fdfbf7', right: '#938a7b', bottom: '#938a7b' },
  /**
   * Was identical to `panel`, which flattened secondary buttons into plain
   * panels. One step below it, so the two-tier surface hierarchy survives in
   * both shells.
   */
  panelAlt: { bg: '#efeade', top: '#f8f5ec', left: '#f4f0e6', right: '#938a7b', bottom: '#938a7b' },
  rule: '#938a7b',
  text: '#1b2733',
  textDim: '#5f584e',
  /** Was `#8a8072` — 3.4:1 on paper. */
  textLabel: '#63594c',
  /** Was `#a49b8b` — 2.4:1 on paper. */
  textFaint: '#6f6659',
  accent: '#765900',
  accentFaces: faces('#efdc38'),
  onAccent: '#1b2733',
  onAccentDim: '#625400',
  onColor: '#1b2733',
  scrim: 'rgba(245,241,232,0.93)',
  ghost: {
    bg: 'rgba(27,39,51,0.05)',
    top: 'rgba(27,39,51,0.18)',
    left: 'rgba(27,39,51,0.12)',
    right: 'rgba(27,39,51,0.07)',
    bottom: 'rgba(27,39,51,0.05)',
  },
  highlightRow: 'rgba(239,220,56,0.45)',
  clearBand: 'rgba(239,220,56,0.30)',
  levelFlash: 'rgba(239,220,56,0.22)',
};

export const SHELLS: Record<ShellTheme, ShellPalette> = { night: NIGHT, day: DAY };

let currentShell: ShellTheme = 'night';

/** Which shell is being painted. */
export function activeShell(): ShellTheme {
  return currentShell;
}

export function setShell(shell: ShellTheme): void {
  currentShell = shell;
}

/** The live palette. Renderers read this once per draw, never cache it. */
export function palette(): ShellPalette {
  return SHELLS[currentShell];
}

/** Bevel inset for a block of `size` px: 4 on a 30px cell, 2 on a 13px one. */
export function bevelInset(size: number): number {
  return Math.max(2, Math.round(size * 0.13));
}

export const LAYOUT = {
  pad: 32,
  cell: 30,
  cols: TUNING.board.width,
  rows: TUNING.board.visibleHeight,
  leftWidth: 120,
  rightWidth: 150,
  gap: 24,
  /**
   * Was 24; the 3px rule and the 6px well frame need the extra room between
   * the header and the top of the field.
   */
  headerHeight: 34,
  miniCell: 13,
  /** Was 44; the 30px it frees is what the fixed HUD slots below need. */
  nextRowHeight: 38,
  /** Was 1px; the block language needs a heavier rule. */
  ruleHeight: 3,
  /** Bevel around the playfield well. */
  frame: 6,
} as const;

export const FIELD_W = LAYOUT.cell * LAYOUT.cols;
export const FIELD_H = LAYOUT.cell * LAYOUT.rows;
export const FIELD_X = LAYOUT.pad + LAYOUT.leftWidth + LAYOUT.gap;
export const FIELD_Y = LAYOUT.pad + LAYOUT.headerHeight;
export const RIGHT_X = FIELD_X + FIELD_W + LAYOUT.gap;

/** Logical (unscaled) canvas size every screen is designed against. */
export const CANVAS_WIDTH = FIELD_X + FIELD_W + LAYOUT.gap + LAYOUT.rightWidth + LAYOUT.pad;
export const CANVAS_HEIGHT = FIELD_Y + FIELD_H + LAYOUT.frame + LAYOUT.pad;

export const FONT_FAMILY = '"JetBrains Mono", ui-monospace, monospace';

/**
 * Heaviest bundled face. The design calls for 800 on headings, scores, buttons
 * and chips; with only 400/500/700 in `src/fonts/` those map onto 700 rather
 * than letting the browser synthesise a fake bold.
 */
export const MAX_WEIGHT = 700;

export function weight(value: number): number {
  return Math.min(value, MAX_WEIGHT);
}

/** size / weight / tracking triples used by the retheme. */
export const TYPE = {
  wordmark: { size: 40, weight: 800, tracking: 0.1 },
  subtitle: { size: 10, weight: 500, tracking: 0.28 },
  screenTitle: { size: 30, weight: 800, tracking: 0.16 },
  overlayTitle: { size: 22, weight: 800, tracking: 0.26 },
  scoreValue: { size: 26, weight: 800, tracking: 0.02 },
  bigScore: { size: 30, weight: 800, tracking: 0.04 },
  levelNumeral: { size: 44, weight: 800, tracking: 0.02 },
  readout: { size: 18, weight: 700, tracking: 0.04 },
  buttonPrimary: { size: 15, weight: 800, tracking: 0.18 },
  buttonSecondary: { size: 15, weight: 700, tracking: 0.18 },
  buttonKey: { size: 11, weight: 700, tracking: 0.1 },
  label: { size: 10, weight: 700, tracking: 0.22 },
  keyName: { size: 10, weight: 500, tracking: 0.14 },
  keyValue: { size: 10, weight: 700, tracking: 0.08 },
  chip: { size: 9, weight: 800, tracking: 0.14 },
  status: { size: 10, weight: 700, tracking: 0.2 },
  brand: { size: 12, weight: 700, tracking: 0.24 },
  /** Was 9 — below the readable floor for hint text. */
  footer: { size: 10, weight: 500, tracking: 0.2 },
} as const;

// ---------------------------------------------------------------- HUD slots

/**
 * The game screen's right column is anchored, not accumulated: nothing
 * inserted above the award plaque may push it past the bottom pad. All values
 * are absolute y in logical canvas units and every height is fixed.
 */
export const HUD_SLOTS = {
  next: { y: 82, h: 202 },
  score: { y: 302, h: 66 },
  stats: { y: 376, h: 92 },
  /** `GameStats` counters. BALANCE no longer shares this column. */
  run: { y: 476, h: 50 },
  chips: { y: 534, h: 22 },
  award: { y: 566, h: 74 },
} as const;

/** Three label/value rows inside the stats panel, 26px pitch. */
export const STATS_ROWS = {
  x: 514,
  width: 122,
  firstY: 390,
  pitch: 26,
  labelSize: 9,
  valueSize: 18,
  /** BEST is the quiet one. */
  secondarySize: 14,
} as const;

// ------------------------------------------------------------ balance stage

/**
 * BALANCE is its own stage, not a variant of the game screen — a literal tilt
 * cannot fit inside Solo's three-column shell. Own renderer, own cell size, own
 * pivot; the board and the beam rotate as one body about `pivot`.
 *
 * Swept box across the full ±`maxTiltDegrees` range: x 148–534, y 120–641.
 */
export const BALANCE_STAGE = {
  cell: 24,
  frame: 5,
  /** Field box; the well frame sits `frame` px outside it. */
  field: { x: 221, y: 138, w: 240, h: 480 },
  /** Shared rotation origin for the board and the beam. */
  pivot: { x: 341, y: 630 },

  /** Horizontal HUD strip above the topple line. */
  band: {
    identityX: 180,
    hold: { x: 32, labelY: 56, y: 70, w: 44, h: 34 },
    next: { x: 88, labelY: 56, y: 70, w: 200, h: 34, slot: 40 },
    miniCell: 8,
    score: { x: 306, labelY: 56, y: 68 },
    combo: { x: 404, y: 76 },
    readouts: { rightX: 650, ys: [56, 70, 84] },
  },

  /** Full-bleed level reference. At risk 1.0 the high corner reaches it. */
  toppleLine: {
    y: 116,
    h: 3,
    /** Clears the instrument band's boxes, which end at 104. */
    labelY: 104,
    tickXs: [120, 200, 470, 560],
    tickW: 2,
    tickH: 7,
    color: { night: '#3a5063', day: '#c9c1b0' },
  },

  /** Only past `dangerRisk`. Sits in the wedge the tilt opens up. */
  banner: { x: 32, y: 132, w: 208, h: 26 },

  /**
   * One block per four settled cells either side of the pivot. Label and count
   * sit directly above each tower, so their differing heights read as the
   * imbalance before any number is parsed.
   */
  towers: {
    cellsPerBlock: 4,
    block: 24,
    pitch: 26,
    /** Top edge of the bottom-most block; the stack grows upward from here. */
    baseY: 616,
    left: { x: 44, color: '#63c9d8', ink: { night: '#63c9d8', day: '#2f8b9c' } },
    right: { x: 614, color: '#e72f6f', ink: { night: '#e72f6f', day: '#c11f58' } },
  },

  /** The beam is the gauge; its ends travel ±170·sin θ. No separate meter. */
  beam: { x: 171, y: 624, w: 340, h: 12, color: '#63c9d8' },
  /**
   * Static stepped pyramid; the apex meets the beam's underside at y 636. Mount
   * colour, one value below the beam — a `panelAlt` pyramid measures 1.1:1
   * against either shell and vanishes.
   */
  fulcrum: {
    ys: [636, 648, 660],
    widths: [16, 32, 48],
    h: 12,
    centerX: 341,
    color: '#3f8fa0',
  },
  /** Same value as the mount in both shells; `rule` is invisible on open ground. */
  floor: { y: 672, h: 4, color: '#3f8fa0' },

  /** The frame goes magenta and the status chip reads TIPPING past this. */
  dangerRisk: 0.8,
  /** Readable magenta for text, per shell. */
  dangerInk: { night: '#e72f6f', day: '#c11f58' },
  /**
   * The well frame past `dangerRisk` — all four faces, so it reads as the ring
   * glowing. `bg` stays the shell's field colour: only the frame recolours,
   * never the interior, or the 5%-alpha ghost cells stain.
   */
  dangerFrame: {
    night: { bg: '#0f1922', top: '#ec598c', right: '#b42457', bottom: '#a01f4d', left: '#e94a80' },
    day: { bg: '#e5e0d5', top: '#ec598c', right: '#d02a64', bottom: '#b42457', left: '#e94a80' },
  },
} as const;

// -------------------------------------------------------------- mode & board

/** Chip hue per mode, on the scoreboard and the in-game mode chip. */
export const MODE_COLORS: Record<'SOLO' | 'BALANCE' | 'TWIN', string> = {
  SOLO: '#63c9d8',
  BALANCE: '#8c54d0',
  TWIN: '#e95f2e',
};

/** Mode chip hue. Legacy entries carry no `mode` and read as solo runs. */
export function modeColor(mode: string | undefined): string {
  return MODE_COLORS[(mode ?? 'SOLO') as keyof typeof MODE_COLORS] ?? MODE_COLORS.SOLO;
}

/**
 * Scoreboard geometry. A TWIN run sums two boards, so it always outranks a
 * comparable SOLO run — the filter tabs and the per-row chip are what make the
 * single table honest.
 */
export const SCOREBOARD_LAYOUT = {
  tabs: { y: 124, h: 30, padX: 14, xs: [32, 104, 186, 296] },
  headY: 174,
  ruleY: 190,
  firstRowY: 203,
  rowHeight: 32,
  rowGap: 5,
  badge: 26,
  cols: { rank: 32, name: 76, mode: 288, score: 414, lines: 474, level: 522, date: 650 },
} as const;

export const TWIN_LAYOUT = {
  bannerY: 64,
  headerY: 84,
  identity: 22,
  fieldY: 112,
  frame: 5,
  cell: 24,
  summaryY: 606,
  boards: [
    { x: 87, label: 'A', color: '#63c9d8', railX: 32, rightAligned: false },
    { x: 355, label: 'B', color: '#e95f2e', railX: 608, rightAligned: true },
  ],
  /** A topped-out board's identity block and score. */
  deadAlpha: 0.4,
} as const;

/** Game over: the run summary grid and the three full-width exits. */
export const GAME_OVER = {
  titleY: 150,
  subtitleY: 179,
  plaqueY: 192,
  plaqueH: 62,
  totalsY: 277,
  rankY: 290,
  rankH: 40,
  runLabelY: 364,
  statsGrid: { dx: [-136, 8], ys: [372, 418, 464], w: 136, h: 40 },
  actions: { dx: -136, w: 272, h: 40, ys: [522, 570, 618] },
} as const;

/** Menu key reference — was 8px, below the readable floor. */
export const MENU_KEYS = {
  x: 32,
  colWidth: 152,
  rowWidth: 144,
  labelY: 546,
  firstY: 564,
  pitch: 18,
  size: 10,
} as const;

/** Guideline tetromino hues, in the order rank badges cycle through them. */
export const BADGE_COLORS: readonly string[] = [
  '#efdc38',
  '#63c9d8',
  '#92c745',
  '#e95f2e',
  '#4479bd',
  '#e72f6f',
  '#8c54d0',
  '#63c9d8',
  '#92c745',
  '#4479bd',
];

/** HUD readouts: at least two digits, so the column never jitters. */
export function formatCounter(value: number): string {
  return value.toString().padStart(2, '0');
}

/** Scoreboard figures: grouped thousands. */
export function formatScore(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `7:04` — run length, minutes and zero-padded seconds. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
}

/** `2026-07-27` — sortable, unambiguous, no locale surprises. */
export function formatDate(epochMs: number): string {
  const d = new Date(epochMs);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
